import React, { useEffect, useState } from "react";
import {
  BookOpen,
  ExternalLink,
  FileJson,
  History,
  Package,
  RefreshCw,
} from "lucide-react";
import {
  getProductDppDocumentation,
  getProductDppEditions,
  getProductDppEvents,
  unwrapDppDocPayload,
} from "../../../../../apis/Product/ProductEPDApi";

function formatDate(value) {
  if (!value) return "—";
  const d = new Date(value);
  return isNaN(d.getTime())
    ? value
    : d.toLocaleString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
}

function formatNumber(value) {
  if (value == null || value === "") return "—";
  const n = Number(value);
  return Number.isFinite(n) ? n.toLocaleString("en-GB") : String(value);
}

const statusBadgeClass = {
  active: "text-emerald-300 bg-emerald-400/15 border-emerald-400/30",
  superseded: "text-white/60 bg-white/5 border-white/15",
  draft: "text-amber-300 bg-amber-400/15 border-amber-400/30",
  revoked: "text-red-300 bg-red-400/15 border-red-400/30",
  expired: "text-amber-200 bg-amber-500/20 border-amber-400/40",
};

function StatusPill({ status }) {
  if (!status) return null;
  const cls = statusBadgeClass[status] || statusBadgeClass.superseded;
  return (
    <span
      className={`inline-flex items-center text-[10px] font-semibold px-2 py-0.5 rounded-full border capitalize ${cls}`}
    >
      {status}
    </span>
  );
}

function EventTypeBadge({ eventType }) {
  const isRegistry = /registry/i.test(eventType || "");
  return (
    <span
      className={`inline-flex text-[10px] font-mono px-1.5 py-0.5 rounded border ${
        isRegistry
          ? "text-sky-300 bg-sky-400/10 border-sky-400/30"
          : "text-white/70 bg-white/5 border-white/15"
      }`}
    >
      {eventType || "—"}
    </span>
  );
}

/**
 * Documentation layer panel — editions, units out, audit timeline, client links.
 * Visible when DPP is published (same gate as DPPPanel).
 */
const DPPDocumentationPanel = ({
  productId,
  businessId,
  dppData,
  lcaLockStatus,
  refreshTrigger = 0,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [summary, setSummary] = useState(null);
  const [editions, setEditions] = useState([]);
  const [events, setEvents] = useState([]);

  const isEpdLocked = lcaLockStatus === "epd_locked";
  const isPublished = !!(dppData?.dpp_is_published || dppData?.dpp_uid);

  useEffect(() => {
    if (!productId || !businessId || !isEpdLocked || !isPublished) return;

    let cancelled = false;
    setLoading(true);
    setError(null);

    let pending = 3;
    const done = () => {
      pending -= 1;
      if (pending <= 0 && !cancelled) setLoading(false);
    };

    getProductDppDocumentation({
      productId,
      businessId,
      onSuccess: (res) => {
        if (cancelled) return;
        setSummary(unwrapDppDocPayload(res));
        done();
      },
      onError: (err) => {
        if (cancelled) return;
        setError(
          err?.response?.data?.message || "Could not load documentation summary."
        );
        done();
      },
    });

    getProductDppEditions({
      productId,
      businessId,
      onSuccess: (res) => {
        if (cancelled) return;
        const payload = unwrapDppDocPayload(res);
        const list = Array.isArray(payload)
          ? payload
          : payload?.results ?? payload?.editions ?? [];
        setEditions(list);
        done();
      },
      onError: () => {
        if (!cancelled) setEditions([]);
        done();
      },
    });

    getProductDppEvents({
      productId,
      businessId,
      onSuccess: (res) => {
        if (cancelled) return;
        const payload = unwrapDppDocPayload(res);
        const list = Array.isArray(payload)
          ? payload
          : payload?.results ?? payload?.events ?? [];
        setEvents(list);
        done();
      },
      onError: () => {
        if (!cancelled) setEvents([]);
        done();
      },
    });

    return () => {
      cancelled = true;
    };
  }, [productId, businessId, isEpdLocked, isPublished, refreshTrigger]);

  if (!isEpdLocked || !isPublished) return null;

  const currentEdition =
    summary?.current_edition ??
    editions.find((e) => e.status === "active") ??
    editions[0] ??
    null;

  const units = summary?.units ?? null;
  const unitsOut = units?.units_out ?? units?.unitsOut;
  const modelNumber =
    currentEdition?.product_num_snapshot ??
    currentEdition?.model_number ??
    units?.model_number ??
    summary?.product_num ??
    "—";

  const activeFrom =
    summary?.active_from ??
    currentEdition?.active_from ??
    currentEdition?.activated_at ??
    null;

  const effectiveStatus =
    summary?.effective_status ?? currentEdition?.status ?? null;

  const publicDocUrl =
    summary?.documentation_url ??
    (dppData?.dpp_url
      ? `${String(dppData.dpp_url).replace(/\/$/, "")}/documentation`
      : null);

  const breakdown = units?.breakdown;

  return (
    <div
      className="rounded-3xl overflow-hidden shadow-lg mt-6"
      style={{ background: "hsl(221 39% 24%)" }}
    >
      <div className="p-8 space-y-4">
        <div className="flex items-center gap-2">
          <BookOpen className="h-4 w-4 text-white/70 flex-shrink-0" />
          <h3 className="text-sm font-semibold text-white">Documentation</h3>
          {loading && (
            <RefreshCw className="h-3 w-3 text-white/40 animate-spin ml-1" />
          )}
          {effectiveStatus && (
            <span className="ml-auto">
              <StatusPill status={effectiveStatus} />
            </span>
          )}
        </div>

        <p className="text-[10px] text-white/45 leading-relaxed">
          Editions, active period, declared units, and audit log. Clients see the
          public subset on the passport URL / QR — no separate portal.
        </p>

        {error && (
          <p className="text-[11px] text-amber-300/90 bg-amber-400/10 border border-amber-400/20 rounded-xl px-3 py-2">
            {error}
          </p>
        )}

        {/* Current edition + units */}
        <div className="rounded-xl bg-white/5 border border-white/10 p-3 space-y-2 text-xs">
          <div className="flex items-center justify-between gap-2">
            <span className="text-white/50 uppercase tracking-wide text-[10px]">
              Active edition
            </span>
            <span className="text-white font-semibold">
              {currentEdition?.edition_number != null
                ? `#${currentEdition.edition_number}`
                : currentEdition?.label || "—"}
            </span>
          </div>
          <div className="flex items-center justify-between gap-2">
            <span className="text-white/50 uppercase tracking-wide text-[10px]">
              Active from
            </span>
            <span className="text-white/80">{formatDate(activeFrom)}</span>
          </div>
          <div className="flex items-center justify-between gap-2">
            <span className="text-white/50 uppercase tracking-wide text-[10px]">
              Model number
            </span>
            <span className="text-white font-mono text-[11px]">{modelNumber}</span>
          </div>
          <div className="pt-1 border-t border-white/10 flex items-start justify-between gap-2">
            <span className="text-white/50 uppercase tracking-wide text-[10px] flex items-center gap-1">
              <Package className="h-3 w-3" />
              Units out
            </span>
            <div className="text-right">
              <div className="text-white font-semibold">
                {formatNumber(unitsOut)}
                {units?.unit ? (
                  <span className="text-white/50 font-normal ml-1">{units.unit}</span>
                ) : null}
              </div>
              {breakdown && (
                <div className="text-[10px] text-white/45 mt-0.5">
                  sold {formatNumber(breakdown.sold)} · leased{" "}
                  {formatNumber(breakdown.leased)} · takeback{" "}
                  {formatNumber(breakdown.takeback)}
                </div>
              )}
              {units?.source && (
                <div className="text-[9px] text-white/35 mt-0.5 max-w-[180px]">
                  {units.source}
                  {units.note ? ` — ${units.note}` : ""}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Client access */}
        <div className="grid grid-cols-1 gap-2">
          {dppData?.dpp_url && (
            <a
              href={dppData.dpp_url}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full h-10 text-xs font-semibold border border-white/20 text-white bg-transparent hover:bg-white/10 rounded-xl flex items-center justify-center gap-1.5 no-underline"
            >
              <ExternalLink className="h-3.5 w-3.5" />
              View public passport
            </a>
          )}
          {publicDocUrl && (
            <a
              href={publicDocUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full h-10 text-xs font-semibold border border-emerald-500/40 text-emerald-300 bg-transparent hover:bg-emerald-500/10 rounded-xl flex items-center justify-center gap-1.5 no-underline"
            >
              <FileJson className="h-3.5 w-3.5" />
              Documentation JSON
            </a>
          )}
        </div>

        {/* Editions table */}
        <div className="space-y-2">
          <div className="flex items-center gap-1.5">
            <History className="h-3.5 w-3.5 text-white/50" />
            <p className="text-[10px] text-white/50 uppercase tracking-wide font-medium">
              Editions
            </p>
          </div>
          {editions.length === 0 ? (
            <p className="text-[11px] text-white/40 italic">No editions yet.</p>
          ) : (
            <div className="rounded-xl border border-white/10 overflow-hidden max-h-48 overflow-y-auto">
              <table className="w-full text-[11px]">
                <thead>
                  <tr className="bg-white/5 text-white/50 text-left">
                    <th className="p-2 font-medium">#</th>
                    <th className="p-2 font-medium">Status</th>
                    <th className="p-2 font-medium">Active from</th>
                    <th className="p-2 font-medium">Model</th>
                  </tr>
                </thead>
                <tbody>
                  {editions.map((ed) => (
                    <tr
                      key={ed.id || `${ed.edition_number}-${ed.active_from}`}
                      className="border-t border-white/10 text-white/80"
                    >
                      <td className="p-2 font-semibold">
                        {ed.edition_number ?? "—"}
                      </td>
                      <td className="p-2">
                        <StatusPill status={ed.status} />
                      </td>
                      <td className="p-2 text-white/60">
                        {formatDate(ed.active_from ?? ed.activated_at)}
                      </td>
                      <td className="p-2 font-mono text-[10px]">
                        {ed.product_num_snapshot ?? ed.model_number ?? "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Events timeline */}
        <div className="space-y-2">
          <p className="text-[10px] text-white/50 uppercase tracking-wide font-medium">
            Documentation log
          </p>
          {events.length === 0 ? (
            <p className="text-[11px] text-white/40 italic">No events yet.</p>
          ) : (
            <ul className="space-y-2 max-h-56 overflow-y-auto pr-1">
              {events.map((ev) => (
                <li
                  key={ev.id || `${ev.event_type}-${ev.occurred_at}`}
                  className="rounded-xl bg-white/5 border border-white/10 px-3 py-2"
                >
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <EventTypeBadge eventType={ev.event_type} />
                    <span className="text-[10px] text-white/40">
                      {formatDate(ev.occurred_at)}
                    </span>
                  </div>
                  <p className="text-[11px] text-white/80 leading-snug">
                    {ev.summary || "—"}
                  </p>
                  {ev.actor?.name && (
                    <p className="text-[10px] text-white/40 mt-1">
                      by {ev.actor.name}
                    </p>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default DPPDocumentationPanel;
